// Table tools for the EasyMDE / CodeMirror post editor.
//
// Two pieces:
//   1. insertTableAction(editor)  — toolbar button: opens a Google-Docs-style
//      grid picker (hover to choose N columns × M rows) and inserts a clean
//      GitHub-flavoured markdown table at the cursor.
//   2. attachTableTools(editor)   — a small floating toolbar that appears
//      whenever the caret sits inside a markdown table, with buttons to add /
//      remove a row or a column relative to the current cell — so the table
//      can be reshaped right in the editor without touching the pipes by hand.
//
// Markdown tables are plain text, so everything below is just careful
// string surgery on the contiguous block of `|`-containing lines around the
// caret. The mistune `table` plugin (already enabled) renders the result.

const MAX_GRID = 10; // picker grid size (cols × rows)

/* ───────────────────────── markdown helpers ───────────────────────── */

function buildTableMarkdown(cols, dataRows) {
    const header = "| " + Array.from({ length: cols }, (_, i) => `Заголовок ${i + 1}`).join(" | ") + " |";
    const sep = "| " + Array.from({ length: cols }, () => "---").join(" | ") + " |";
    const body = Array.from(
        { length: dataRows },
        () => "| " + Array.from({ length: cols }, () => "  ").join(" | ") + " |"
    ).join("\n");
    return dataRows > 0 ? `${header}\n${sep}\n${body}` : `${header}\n${sep}`;
}

function isTableLine(line) {
    return line != null && line.indexOf("|") !== -1;
}

// Returns {start, end} line range of the table block under the caret, or null.
function tableBlockAt(cm) {
    const doc = cm.getDoc();
    const cur = doc.getCursor();
    if (!isTableLine(doc.getLine(cur.line))) return null;
    let start = cur.line;
    let end = cur.line;
    while (start > 0 && isTableLine(doc.getLine(start - 1))) start--;
    while (end < doc.lineCount() - 1 && isTableLine(doc.getLine(end + 1))) end++;
    // A real table needs at least a header + separator row.
    if (end - start < 1) return null;
    return { start, end };
}

function splitRow(line) {
    let s = line.trim();
    if (s.startsWith("|")) s = s.slice(1);
    if (s.endsWith("|")) s = s.slice(0, -1);
    return s.split("|").map((c) => c.trim());
}

function joinRow(cells) {
    return "| " + cells.join(" | ") + " |";
}

function isSeparatorRow(cells) {
    return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c.replace(/\s/g, "")));
}

// Which cell index the caret is in, on its current line.
function cellIndexAt(cm) {
    const cur = cm.getDoc().getCursor();
    const line = cm.getDoc().getLine(cur.line) || "";
    const before = line.slice(0, cur.ch);
    let idx = (before.match(/\|/g) || []).length - 1;
    if (idx < 0) idx = 0;
    return idx;
}

function getBlockLines(cm, block) {
    const doc = cm.getDoc();
    const lines = [];
    for (let i = block.start; i <= block.end; i++) lines.push(doc.getLine(i));
    return lines;
}

function replaceBlock(cm, block, newText) {
    const doc = cm.getDoc();
    doc.replaceRange(
        newText,
        { line: block.start, ch: 0 },
        { line: block.end, ch: doc.getLine(block.end).length }
    );
}

/* ───────────────────────── table mutations ───────────────────────── */

function addColumn(cm) {
    const block = tableBlockAt(cm);
    if (!block) return;
    const at = cellIndexAt(cm) + 1; // insert to the RIGHT of the current cell
    const out = getBlockLines(cm, block).map((line) => {
        const cells = splitRow(line);
        const insertAt = Math.min(Math.max(at, 0), cells.length);
        cells.splice(insertAt, 0, isSeparatorRow(cells) ? "---" : "  ");
        return joinRow(cells);
    });
    replaceBlock(cm, block, out.join("\n"));
    cm.focus();
}

function deleteColumn(cm) {
    const block = tableBlockAt(cm);
    if (!block) return;
    const at = cellIndexAt(cm);
    const lines = getBlockLines(cm, block);
    const colCount = splitRow(lines[0]).length;
    if (colCount <= 1) return; // never leave a table with zero columns
    const out = lines.map((line) => {
        const cells = splitRow(line);
        if (at < cells.length) cells.splice(at, 1);
        return joinRow(cells);
    });
    replaceBlock(cm, block, out.join("\n"));
    cm.focus();
}

function addRow(cm) {
    const block = tableBlockAt(cm);
    if (!block) return;
    const doc = cm.getDoc();
    const cur = doc.getCursor();
    const cols = splitRow(doc.getLine(block.start)).length;
    const empty = joinRow(Array.from({ length: cols }, () => "  "));
    // Insert the new row right below the caret's row, but never above the
    // separator (rows 0 = header, 1 = separator).
    let insertLine = Math.max(cur.line, block.start + 1);
    doc.replaceRange("\n" + empty, { line: insertLine, ch: doc.getLine(insertLine).length });
    cm.setCursor({ line: insertLine + 1, ch: 2 });
    cm.focus();
}

function deleteRow(cm) {
    const block = tableBlockAt(cm);
    if (!block) return;
    const doc = cm.getDoc();
    const cur = doc.getCursor();
    // Can't delete the header or the separator row.
    if (cur.line <= block.start + 1) return;
    if (block.end <= block.start + 1) return; // no body rows
    doc.replaceRange(
        "",
        { line: cur.line, ch: 0 },
        { line: cur.line + 1, ch: 0 }
    );
    cm.focus();
}

// Set the text alignment of the column the caret is in by rewriting that
// column's marker in the separator row (markdown-native: :--- / :---: / ---:).
function setColumnAlign(cm, align) {
    const block = tableBlockAt(cm);
    if (!block) return;
    const doc = cm.getDoc();
    const sepLine = block.start + 1;
    const at = cellIndexAt(cm);
    const cells = splitRow(doc.getLine(sepLine));
    if (at >= cells.length) return;
    cells[at] = align === "center" ? ":---:" : align === "right" ? "---:" : ":---";
    doc.replaceRange(
        joinRow(cells),
        { line: sepLine, ch: 0 },
        { line: sepLine, ch: doc.getLine(sepLine).length }
    );
    cm.focus();
}

// Set / clear the background color of the caret's row via a {bg:COLOR} marker
// in the row's first cell (markdown.py promotes it to a <tr> background).
function setRowBg(cm, color) {
    const block = tableBlockAt(cm);
    if (!block) return;
    const doc = cm.getDoc();
    const cur = doc.getCursor();
    if (cur.line === block.start + 1) return; // not the separator row
    const cells = splitRow(doc.getLine(cur.line));
    if (!cells.length) return;
    cells[0] = cells[0].replace(/\{bg:[^}]*\}\s*/g, "").trim();
    if (color) cells[0] = `{bg:${color}} ` + cells[0];
    doc.replaceRange(
        joinRow(cells),
        { line: cur.line, ch: 0 },
        { line: cur.line, ch: doc.getLine(cur.line).length }
    );
    cm.focus();
}

// Preset row tints — subtle 8-digit-hex (with alpha) so they read on both
// the light and the dark post themes.
const ROW_BG_PRESETS = [
    { color: "#ff646433", label: "🟥" },
    { color: "#4caf7833", label: "🟩" },
    { color: "#ffc93033", label: "🟨" },
    { color: "#5b8def33", label: "🟦" },
];

/* ───────────────────────── grid picker UI ───────────────────────── */

function openGridPicker(anchorEl, onPick) {
    // Tear down any existing picker first.
    const existing = document.querySelector(".md-table-picker");
    if (existing) existing.remove();

    const pop = document.createElement("div");
    pop.className = "md-table-picker";
    pop.innerHTML =
        '<div class="md-table-picker-grid"></div>' +
        '<div class="md-table-picker-label">Наведіть і клацніть</div>';
    document.body.appendChild(pop);

    const grid = pop.querySelector(".md-table-picker-grid");
    const label = pop.querySelector(".md-table-picker-label");
    const cells = [];
    for (let r = 0; r < MAX_GRID; r++) {
        for (let col = 0; col < MAX_GRID; col++) {
            const cell = document.createElement("span");
            cell.className = "md-table-picker-cell";
            cell.dataset.r = r;
            cell.dataset.c = col;
            grid.appendChild(cell);
            cells.push(cell);
        }
    }

    function highlight(rr, cc) {
        cells.forEach((cell) => {
            const on = +cell.dataset.r <= rr && +cell.dataset.c <= cc;
            cell.classList.toggle("is-on", on);
        });
        // columns × rows (rows here = header + body, so user sees total rows)
        label.textContent = `${cc + 1} × ${rr + 1}`;
    }

    grid.addEventListener("mouseover", (e) => {
        const cell = e.target.closest(".md-table-picker-cell");
        if (cell) highlight(+cell.dataset.r, +cell.dataset.c);
    });
    grid.addEventListener("click", (e) => {
        const cell = e.target.closest(".md-table-picker-cell");
        if (!cell) return;
        const cols = +cell.dataset.c + 1;
        const totalRows = +cell.dataset.r + 1; // includes the header row
        onPick(cols, Math.max(totalRows - 1, 0));
        pop.remove();
    });

    // Position under the toolbar button.
    const rect = anchorEl.getBoundingClientRect();
    pop.style.top = `${window.scrollY + rect.bottom + 6}px`;
    pop.style.left = `${window.scrollX + rect.left}px`;

    // Dismiss on outside click / escape.
    const close = (ev) => {
        if (ev.type === "keydown" && ev.key !== "Escape") return;
        if (ev.type === "mousedown" && pop.contains(ev.target)) return;
        pop.remove();
        document.removeEventListener("mousedown", close, true);
        document.removeEventListener("keydown", close, true);
    };
    setTimeout(() => {
        document.addEventListener("mousedown", close, true);
        document.addEventListener("keydown", close, true);
    }, 0);
}

/* ───────────────────────── public API ───────────────────────── */

// The 2nd line of a table block must be a pure separator (`| --- | --- |`).
// Users routinely click into it and type, which silently kills the whole
// table (mistune stops recognising it). This rewrites a corrupted separator
// back to the right number of dashes — but never while the caret is parked
// on the separator line itself (so it doesn't fight an in-progress edit).
function healSeparator(cm) {
    const block = tableBlockAt(cm);
    if (!block) return;
    const doc = cm.getDoc();
    const sepLine = block.start + 1;
    const cols = splitRow(doc.getLine(block.start)).length;
    const sepCells = splitRow(doc.getLine(sepLine));
    const valid =
        sepCells.length === cols &&
        sepCells.every((cell) => /^:?-{1,}:?$/.test(cell.replace(/\s/g, "")));
    if (valid) return;
    if (doc.getCursor().line === sepLine) return; // don't interrupt a live edit
    const fixed = joinRow(Array.from({ length: cols }, () => "---"));
    doc.replaceRange(
        fixed,
        { line: sepLine, ch: 0 },
        { line: sepLine, ch: doc.getLine(sepLine).length }
    );
}

// Toolbar action — `this`/arg is the EasyMDE instance.
export function insertTableAction(editor) {
    const cm = editor.codemirror;
    // Anchor the picker under the clicked toolbar button.
    const anchor =
        editor.toolbarElements && editor.toolbarElements.table
            ? editor.toolbarElements.table
            : document.querySelector(".editor-toolbar .fa-table") || document.body;

    openGridPicker(anchor, (cols, dataRows) => {
        const doc = cm.getDoc();
        const cur = doc.getCursor();
        const curLine = doc.getLine(cur.line) || "";
        const prevLine = cur.line > 0 ? doc.getLine(cur.line - 1) || "" : "";

        // mistune ONLY parses a table when a blank line sits right before it.
        // Compute the leading newlines needed to guarantee that blank line in
        // every caret position.
        let lead;
        if (curLine.slice(0, cur.ch).trim() !== "" || curLine.trim() !== "") {
            lead = "\n\n"; // caret on a line that has text → break to a fresh blank line
        } else if (prevLine.trim() !== "") {
            lead = "\n"; // on a blank line but text directly above → need one blank line
        } else {
            lead = ""; // already isolated by blank space (or top of an empty doc)
        }

        const table = buildTableMarkdown(cols, dataRows);
        doc.replaceSelection(lead + table + "\n\n"); // trailing blank line too

        // Caret → first BODY cell (so the user types data, not the separator).
        const leadLines = (lead.match(/\n/g) || []).length;
        const headerLine = cur.line + leadLines;
        const targetLine = dataRows > 0 ? headerLine + 2 : headerLine;
        cm.setCursor({ line: targetLine, ch: 2 });
        cm.focus();
    });
}

// Floating "in-table" toolbar with add/remove row & column controls.
export function attachTableTools(editor) {
    const cm = editor.codemirror;
    const container = editor.element.closest(".EasyMDEContainer") || cm.getWrapperElement().parentNode;
    if (!container) return;

    const bar = document.createElement("div");
    bar.className = "md-table-bar";
    bar.hidden = true;
    const swatches = ROW_BG_PRESETS.map(
        (p) =>
            `<button type="button" class="md-table-swatch" data-bg="${p.color}" title="Фон рядка"
                     style="background:${p.color}">${p.label}</button>`
    ).join("");
    bar.innerHTML = `
        <span class="md-table-bar-title">Таблиця:</span>
        <button type="button" data-act="add-col" title="Додати стовпець праворуч">＋ стовпець</button>
        <button type="button" data-act="del-col" title="Видалити стовпець">－ стовпець</button>
        <button type="button" data-act="add-row" title="Додати рядок нижче">＋ рядок</button>
        <button type="button" data-act="del-row" title="Видалити рядок">－ рядок</button>
        <span class="md-table-bar-sep"></span>
        <span class="md-table-bar-title">Вирівняти:</span>
        <button type="button" data-align="left"   title="Ліворуч">⬅</button>
        <button type="button" data-align="center" title="По центру">⬌</button>
        <button type="button" data-align="right"  title="Праворуч">➡</button>
        <span class="md-table-bar-sep"></span>
        <span class="md-table-bar-title">Фон рядка:</span>
        ${swatches}
        <button type="button" data-bg="" title="Прибрати фон">✖</button>
    `;
    // Insert the bar right above the editor area.
    container.insertBefore(bar, container.querySelector(".CodeMirror"));

    bar.addEventListener("mousedown", (e) => {
        // Keep the editor selection; act on the button.
        const btn = e.target.closest("button");
        if (!btn) return;
        e.preventDefault();
        if (btn.dataset.act) {
            const act = btn.dataset.act;
            if (act === "add-col") addColumn(cm);
            else if (act === "del-col") deleteColumn(cm);
            else if (act === "add-row") addRow(cm);
            else if (act === "del-row") deleteRow(cm);
        } else if (btn.dataset.align) {
            setColumnAlign(cm, btn.dataset.align);
        } else if (btn.hasAttribute("data-bg")) {
            setRowBg(cm, btn.dataset.bg);
        }
    });

    let healing = false;
    const refresh = () => {
        bar.hidden = !tableBlockAt(cm);
        // Auto-repair a separator the user may have typed over, but guard
        // against the re-entrant change our own replaceRange would trigger.
        if (!healing) {
            healing = true;
            try { healSeparator(cm); } finally { healing = false; }
        }
    };
    cm.on("cursorActivity", refresh);
    cm.on("changes", refresh);
    refresh();
}
